import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { usePrefersReducedMotion } from "@/context/ThemeContext";
import { IMAGES } from "@/data/site";

// Vanilla three.js hero — a rotating low-poly architectural form inside a
// structural particle grid. Written without react-three-fiber on purpose to
// stay compatible with the build pipeline. Progressive: static fallback for
// reduced-motion or WebGL failure.
export function Hero3D() {
  const reduced = usePrefersReducedMotion();
  const mountRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const mount = mountRef.current;
    if (!mount) return;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (e) {
      setFailed(true);
      return;
    }

    const width = mount.clientWidth;
    const height = mount.clientHeight;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    camera.position.z = 7;

    // Particle field
    const count = 700;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 16;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pMat = new THREE.PointsMaterial({ size: 0.035, color: 0x6a6a6a, transparent: true, opacity: 0.7 });
    const points = new THREE.Points(pGeo, pMat);
    scene.add(points);

    // Low-poly architectural form
    const group = new THREE.Group();
    const outer = new THREE.Mesh(
      new THREE.IcosahedronGeometry(2.1, 1),
      new THREE.MeshBasicMaterial({ color: 0x3a3a3a, wireframe: true, transparent: true, opacity: 0.55 })
    );
    const inner = new THREE.Mesh(
      new THREE.OctahedronGeometry(2.1 * 0.62, 0),
      new THREE.MeshBasicMaterial({ color: 0xff4500, wireframe: true, transparent: true, opacity: 0.85 })
    );
    group.add(outer);
    group.add(inner);
    scene.add(group);

    const pointer = { x: 0, y: 0 };
    const onPointer = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener("pointermove", onPointer);

    const clock = { last: performance.now() };
    let raf;
    const animate = () => {
      const now = performance.now();
      const delta = Math.min((now - clock.last) / 1000, 0.1);
      clock.last = now;
      group.rotation.y += delta * 0.18;
      group.rotation.x += (pointer.y * 0.25 - group.rotation.x) * 0.05;
      group.rotation.z += (pointer.x * 0.12 - group.rotation.z) * 0.05;
      points.rotation.y += delta * 0.03;
      points.rotation.x += (pointer.y * 0.1 - points.rotation.x) * 0.03;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("resize", onResize);
      pGeo.dispose();
      pMat.dispose();
      outer.geometry.dispose();
      outer.material.dispose();
      inner.geometry.dispose();
      inner.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [reduced]);

  if (reduced || failed) {
    return (
      <div className="absolute inset-0" aria-hidden>
        <img src={IMAGES.heroFallback} alt="" className="w-full h-full object-cover opacity-40" loading="eager" />
        <div className="absolute inset-0 blueprint-grid opacity-60" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0" aria-hidden data-testid="hero-webgl">
      <div ref={mountRef} className="absolute inset-0" />
      <div className="absolute inset-0 blueprint-grid opacity-30 pointer-events-none" />
    </div>
  );
}
